// DocumentUploader.tsx
import type React from "react";
import { useCallback, useState } from "react";

import { Label } from "@radix-ui/react-label";
import { Loader2, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";

interface DocumentUploaderProps {
  onUpload: (files: File[]) => void;
  isUploading: boolean;
  uploadProgress?: {
    status: "uploading" | "processing" | "embedding" | "completed" | "error";
    progress: number;
    error?: string;
  };
  fileInputRef: React.RefObject<HTMLInputElement>;
}

export function DocumentUploader({
  onUpload,
  isUploading,
  uploadProgress,
  fileInputRef,
}: Readonly<DocumentUploaderProps>) {
  const [filesSelected, setFilesSelected] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleUpload = useCallback(() => {
    if (selectedFiles.length > 0) {
      onUpload(selectedFiles);
      setFilesSelected(false);
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [selectedFiles, onUpload, fileInputRef]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const validFiles = Array.from(files).filter(
        (file) => file.type === "application/pdf",
      );
      setFilesSelected(validFiles.length > 0);
      setSelectedFiles(validFiles);

      if (validFiles.length === 0) {
        alert("Please select PDF files only.");
      }
    } else {
      setFilesSelected(false);
      setSelectedFiles([]);
    }
  };

  const getStatusMessage = () => {
    if (!uploadProgress) return null;

    switch (uploadProgress.status) {
      case "uploading":
        return "Uploading document...";
      case "processing":
        return "Processing document...";
      case "embedding":
        return "Generating AI embeddings...";
      case "completed":
        return "Upload complete!";
      case "error":
        return uploadProgress.error ?? "An error occurred";
      default:
        return null;
    }
  };

  return (
    <div className="max-w-128">
      <h2 className="mb-4 text-lg font-medium">AI Knowledge base</h2>
      <p className="mb-4 text-sm text-gray-400">
        Documents you upload here will be used in Get AI Help to answer
        questions.
      </p>
      <div className="mb-2">
        <Label htmlFor="file">Upload PDFs</Label>
      </div>
      <div className="flex w-full gap-4">
        <div className="flex-grow">
          <Input
            id="file"
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            disabled={isUploading}
            ref={fileInputRef}
            multiple
          />
        </div>
        <Button
          variant="default"
          onClick={handleUpload}
          disabled={isUploading || !filesSelected}
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Upload
            </>
          )}
        </Button>
      </div>

      {uploadProgress && uploadProgress.status !== "completed" && (
        <div className="mt-4">
          <div className="mb-2 flex justify-between text-sm">
            <span>{getStatusMessage()}</span>
            <span>{Math.round(uploadProgress.progress)}%</span>
          </div>
          <Progress value={uploadProgress.progress} className="w-full" />
        </div>
      )}

      {uploadProgress?.status === "error" && (
        <p className="mt-2 text-sm text-red-500">{uploadProgress.error}</p>
      )}
    </div>
  );
}
